import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  Select as ChakraSelect,
  Textarea,
  useToast,
  Text,
  Alert,
  AlertIcon,
  Icon,
  Switch,
  Box,
  Avatar,
  useColorModeValue,
  useDisclosure,
} from '@chakra-ui/react';
import { FiUserPlus, FiAlertTriangle, FiUser, FiUsers } from 'react-icons/fi';
import { Select as SearchableSelect } from 'chakra-react-select';
import { useCrmSettings } from '../../contexts/CrmSettingsContext';
import { useAuth } from '../../contexts/AuthContext';
import { useCrmUsers } from '../../hooks/useCrmUsers';
import ContactPicker from './ContactPicker';
import ContactTypeBadges from './ContactTypeBadges';
import { supabase, isSupabaseConfigured } from '../../utils/supabase';

/**
 * Add Deal Party Modal (Deal Team)
 * 
 * Two-step process:
 * 1. Select role
 * 2. Select the person:
 *    - External roles (agents, title company, ...) pick a CONTACT
 *      (with type filtering based on role)
 *    - Internal roles (co-owner, CSC, LOA) pick a CRM USER — adding them
 *      shares the deal with that user (deal_team grant, handled by DB trigger)
 * 
 * Features:
 * - Role-based contact type filtering
 * - Duplicate prevention
 * - Notes field
 * - Primary party designation
 */
export default function AddDealPartyModal({
  isOpen,
  onClose,
  dealId,
  existingParties = [],
  onPartyAdded,
}) {
  const toast = useToast();
  const { currentUser } = useAuth();
  const { dealPartyRoles, contactTypes: configContactTypes } = useCrmSettings();
  const { users: crmUsers } = useCrmUsers();
  
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedContact, setSelectedContact] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null); // { value: uid, label: name } for internal roles
  const [notes, setNotes] = useState('');
  const [isPrimary, setIsPrimary] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { isOpen: isContactPickerOpen, onOpen: onContactPickerOpen, onClose: onContactPickerClose } = useDisclosure();

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedRole('');
      setSelectedContact(null);
      setSelectedUser(null);
      setNotes('');
      setIsPrimary(false);
    }
  }, [isOpen]);

  // Get allowed contact types for selected role
  const getAllowedContactTypes = () => {
    if (!selectedRole) return [];
    const roleConfig = dealPartyRoles?.find(r => r.value === selectedRole);
    return roleConfig?.contactTypesAllowed || [];
  };

  // Check if role already exists (if allowMultiple = false)
  const roleAlreadyExists = () => {
    if (!selectedRole) return false;
    const roleConfig = dealPartyRoles?.find(r => r.value === selectedRole);
    if (roleConfig?.allowMultiple) return false;
    
    return existingParties.some(p => p.role === selectedRole);
  };

  const handleContactSelected = (contact) => {
    setSelectedContact(contact);
    onContactPickerClose();
  };

  const handleSubmit = async () => {
    const roleCfg = dealPartyRoles?.find(r => r.value === selectedRole);
    const isInternalRole = roleCfg?.internal === true;

    if (!selectedRole) {
      toast({
        title: 'Select a role',
        description: 'Please select a role for this party',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    if (isInternalRole ? !selectedUser : !selectedContact) {
      toast({
        title: isInternalRole ? 'Select a team member' : 'Select a contact',
        description: isInternalRole
          ? 'Please select the CRM user for this role'
          : 'Please select a contact to add',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    // Check for duplicate (same person, same role)
    const duplicate = isInternalRole
      ? existingParties.find(p => p.userId === selectedUser.value && p.role === selectedRole)
      : existingParties.find(p => p.contactId === selectedContact.id && p.role === selectedRole);

    if (duplicate) {
      toast({
        title: 'Duplicate party',
        description: isInternalRole
          ? 'This user already has this role on this deal'
          : 'This contact already has this role on this deal',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    if (!isSupabaseConfigured()) {
      toast({
        title: 'Database not configured',
        description: 'Supabase is not configured properly',
        status: 'error',
        duration: 5000,
      });
      return;
    }

    setIsSubmitting(true);
    try {
      // Insert into crm_deal_participants. Internal roles write user_id —
      // the DB trigger then grants the user visibility on this deal
      // (crm_record_shares, granted_via='deal_team').
      const { data, error } = await supabase
        .from('crm_deal_participants')
        .insert({
          deal_id: dealId,
          ...(isInternalRole
            ? { user_id: selectedUser.value }
            : { contact_id: selectedContact.id }),
          role: selectedRole,
          is_primary: isPrimary,
          created_by: currentUser?.uid || null,
        })
        .select(`
          id,
          deal_id,
          contact_id,
          user_id,
          role,
          is_primary,
          created_at,
          crm_contacts(id, first_name, last_name, email, phone, contact_types)
        `)
        .single();

      if (error) {
        throw error;
      }

      // Transform the result to match expected format
      const newParty = {
        id: data.id,
        dealId: data.deal_id,
        contactId: data.contact_id,
        userId: data.user_id,
        isInternal: !!data.user_id,
        role: data.role,
        isPrimary: data.is_primary,
        source: 'manual',
        addedAt: data.created_at,
        contactName: data.user_id
          ? (selectedUser?.label || 'Team member')
          : (data.crm_contacts
            ? `${data.crm_contacts.first_name || ''} ${data.crm_contacts.last_name || ''}`.trim()
            : ''),
        contactEmail: data.user_id ? (selectedUser?.email || '') : data.crm_contacts?.email,
        contactPhone: data.user_id ? '' : data.crm_contacts?.phone,
        contact: data.crm_contacts ? {
          id: data.crm_contacts.id,
          firstName: data.crm_contacts.first_name,
          lastName: data.crm_contacts.last_name,
          email: data.crm_contacts.email,
          phone: data.crm_contacts.phone,
          contactTypes: data.crm_contacts.contact_types || [],
        } : null,
      };

      toast({
        title: isInternalRole ? 'Team member added' : 'Party added',
        description: isInternalRole
          ? `${newParty.contactName} added as ${roleCfg?.label || selectedRole} — they can now see this deal`
          : `${newParty.contactName} added as ${roleCfg?.label || selectedRole}`,
        status: 'success',
        duration: 3000,
      });

      if (onPartyAdded) {
        onPartyAdded({ success: true, party: newParty });
      }
      onClose();
    } catch (error) {
      console.error('Error adding party:', error);
      toast({
        title: 'Error adding party',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const roleConfig = selectedRole ? dealPartyRoles?.find(r => r.value === selectedRole) : null;
  const isInternalRole = roleConfig?.internal === true;

  // CRM users eligible for the selected internal role (exclude ones who
  // already hold it on this deal)
  const userOptions = (crmUsers || [])
    .filter(u => !existingParties.some(p => p.userId === u.uid && p.role === selectedRole))
    .map(u => ({ value: u.uid, label: u.name || u.email, email: u.email }));
  const cardBG = useColorModeValue('gray.50', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered>
        <ModalOverlay backdropFilter="blur(4px)" />
        <ModalContent>
          <ModalHeader>
            <HStack spacing={2}>
              <Icon as={FiUserPlus} />
              <Text>Add Party to Deal</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              {roleAlreadyExists() && (
                <Alert status="warning" borderRadius="md">
                  <AlertIcon />
                  <Text fontSize="sm">
                    This role already exists on this deal. The configuration doesn't allow multiple parties with this role.
                  </Text>
                </Alert>
              )}

              {/* Step 1: Select Role */}
              <FormControl isRequired>
                <FormLabel fontSize="sm">Party Role</FormLabel>
                <ChakraSelect
                  value={selectedRole}
                  onChange={(e) => {
                    setSelectedRole(e.target.value);
                    setSelectedContact(null); // Reset person when role changes
                    setSelectedUser(null);
                  }}
                  placeholder="Select a role..."
                >
                  <optgroup label="Internal team (CRM users)">
                    {dealPartyRoles?.filter(r => r.internal).map(role => (
                      <option key={role.value} value={role.value}>
                        {role.label}
                      </option>
                    ))}
                  </optgroup>
                  <optgroup label="External parties (contacts)">
                    {dealPartyRoles?.filter(r => !r.internal).map(role => (
                      <option key={role.value} value={role.value}>
                        {role.label}
                      </option>
                    ))}
                  </optgroup>
                </ChakraSelect>
                {roleConfig && !isInternalRole && roleConfig.contactTypesAllowed?.length > 0 && (
                  <Text fontSize="xs" color="gray.600" mt={1}>
                    Allowed contact types: {roleConfig.contactTypesAllowed.map(t => {
                      const typeConfig = configContactTypes?.find(ct => ct.value === t);
                      return typeConfig?.label || t;
                    }).join(', ')}
                  </Text>
                )}
                {isInternalRole && (
                  <Text fontSize="xs" color="purple.600" mt={1}>
                    Internal role — assigning it shares this deal with the selected user.
                  </Text>
                )}
              </FormControl>

              {/* Step 2 (internal roles): Select CRM user */}
              {selectedRole && isInternalRole && (
                <FormControl isRequired>
                  <FormLabel fontSize="sm">
                    <HStack spacing={1}>
                      <Icon as={FiUsers} boxSize={3.5} />
                      <Text>Select Team Member</Text>
                    </HStack>
                  </FormLabel>
                  <SearchableSelect
                    value={selectedUser}
                    onChange={setSelectedUser}
                    options={userOptions}
                    placeholder="Select a CRM user…"
                    isClearable
                    size="sm"
                  />
                </FormControl>
              )}

              {/* Step 2 (external roles): Select Contact */}
              {selectedRole && !isInternalRole && (
                <FormControl isRequired>
                  <FormLabel fontSize="sm">Select Contact</FormLabel>
                  {selectedContact ? (
                    <Box
                      p={3}
                      bg={cardBG}
                      borderRadius="md"
                      borderWidth="1px"
                      borderColor={borderColor}
                    >
                      <HStack justify="space-between">
                        <HStack spacing={2}>
                          <Avatar size="sm" name={`${selectedContact.firstName} ${selectedContact.lastName}`} />
                          <VStack align="flex-start" spacing={0}>
                            <Text fontWeight="semibold" fontSize="sm">
                              {selectedContact.firstName} {selectedContact.lastName}
                            </Text>
                            <ContactTypeBadges
                              contactTypes={selectedContact.contactTypes || []}
                              primaryContactType={selectedContact.primaryContactType}
                              size="xs"
                              maxDisplay={2}
                            />
                          </VStack>
                        </HStack>
                        <Button
                          size="xs"
                          variant="outline"
                          onClick={onContactPickerOpen}
                        >
                          Change
                        </Button>
                      </HStack>
                    </Box>
                  ) : (
                    <Button
                      leftIcon={<FiUser />}
                      onClick={onContactPickerOpen}
                      variant="outline"
                      w="full"
                    >
                      Select Contact
                    </Button>
                  )}
                </FormControl>
              )}

              {/* Optional Fields */}
              {(selectedContact || selectedUser) && (
                <>
                  <FormControl>
                    <FormLabel fontSize="sm">Notes (Optional)</FormLabel>
                    <Textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Deal-specific notes about this party..."
                      rows={3}
                      fontSize="sm"
                    />
                  </FormControl>

                  {roleConfig?.allowMultiple && (
                    <FormControl display="flex" alignItems="center" justifyContent="space-between">
                      <FormLabel fontSize="sm" mb={0}>
                        Mark as Primary
                      </FormLabel>
                      <Switch
                        isChecked={isPrimary}
                        onChange={(e) => setIsPrimary(e.target.checked)}
                        colorScheme="blue"
                      />
                    </FormControl>
                  )}
                </>
              )}
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button
              colorScheme="purple"
              onClick={handleSubmit}
              isLoading={isSubmitting}
              loadingText="Adding..."
              isDisabled={!selectedRole || (isInternalRole ? !selectedUser : !selectedContact)}
            >
              {isInternalRole ? 'Add Team Member' : 'Add Party'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Contact Picker (external roles only) */}
      {selectedRole && !isInternalRole && (
        <ContactPicker
          isOpen={isContactPickerOpen}
          onClose={onContactPickerClose}
          onSelectContact={handleContactSelected}
          filterByContactTypes={getAllowedContactTypes()}
          excludeContactIds={existingParties.map(p => p.contactId)}
          title={`Select ${roleConfig?.label || 'Contact'}`}
        />
      )}
    </>
  );
}

